import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import { BookOpen, Home, Info, User, RefreshCw, Target, Heart, ChevronDown, ChevronUp, Calendar, Trophy, Check, ArrowLeft, Play, TrendingUp, Sparkles } from 'lucide-react';
import { curriculumData } from '../data/curriculumOverview';

const CurriculumPage = () => {
  const navigate = useNavigate();
  const { user, userData } = useUser();
  const [selectedAge, setSelectedAge] = useState(null);
  const [expandedQuarter, setExpandedQuarter] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const mainContainerRef = useRef(null);
  const completedLessons = userData?.completedLessons || [];
  const currentWeek = Math.floor(completedLessons.length / 5) + 1;

  const isPhaseActive = (phaseId) => {
    return userData?.selectedAge === phaseId;
  };

  const selectPhase = (ageKey) => {
    setIsTransitioning(true);
    setExpandedQuarter(null);
    setTimeout(() => {
      setSelectedAge(ageKey);
      window.scrollTo({ top: 0, behavior: 'instant' });
      requestAnimationFrame(() => setIsTransitioning(false));
    }, 300);
  };

  const backToOverview = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setSelectedAge(null);
      window.scrollTo({ top: 0, behavior: 'instant' });
      requestAnimationFrame(() => setIsTransitioning(false));
    }, 300);
  };

  // ─── Overview ───────────────────────────────────────────────
  const OverviewSection = () => (
    <div className="space-y-8">
      {/* Hero */}
      <div className="bg-gradient-to-br from-purple-600 via-pink-500 to-rose-500 rounded-2xl p-5 sm:p-10 text-white text-center shadow-xl">
        <div className="mb-4">
          <img src="/assets/Curriculum.webp" alt="Curriculum" className="w-40 h-32 object-contain mx-auto" />
        </div>
        <h1 className="text-4xl md:text-5xl font-bold mb-3">BrightReading Activity Book</h1>
        <p className="text-lg md:text-xl opacity-95 max-w-2xl mx-auto mb-6 leading-relaxed">
          From first sounds to confident reading — a science-backed journey built on play, repetition, and natural development
        </p>
        <div className="flex gap-2 justify-center flex-wrap text-sm">
          <span className="bg-white/20 rounded-full px-4 py-2 font-semibold">Ages 1-5</span>
          <span className="bg-white/20 rounded-full px-4 py-2 font-semibold">4 Phases</span>
          <span className="bg-white/20 rounded-full px-4 py-2 font-semibold">52 Weeks Each</span>
          <span className="bg-white/20 rounded-full px-4 py-2 font-semibold">260 Activities/Year</span>
          <span className="bg-white/20 rounded-full px-4 py-2 font-semibold">10-15 min/day</span>
        </div>
      </div>

      {/* Phase Cards */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <img src="/assets/Curriculum.webp" alt="" className="w-10 h-10 object-contain" />
          Choose Your Child's Phase
        </h2>
        {Object.entries(curriculumData).map(([ageKey, phase]) => (
          <button
            key={ageKey}
            onClick={() => selectPhase(ageKey)}
            className={`w-full bg-white rounded-xl shadow-md hover:shadow-lg transition-all overflow-hidden text-left ${
              isPhaseActive(ageKey) ? 'ring-3 ring-purple-500' : ''
            }`}
          >
            <div className={`h-20 bg-gradient-to-r ${phase.color} flex items-center px-6 gap-4`}>
              <img src={phase.icon} alt={phase.title} className="w-12 h-12 object-contain" />
              <div className="text-white flex-1">
                <h3 className="text-xl font-bold">{phase.title}</h3>
                <p className="text-sm opacity-90">{phase.subtitle}</p>
              </div>
              {isPhaseActive(ageKey) && (
                <div className="bg-white/20 px-4 py-1.5 rounded-full font-bold text-sm flex items-center gap-2 text-white">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  Active
                </div>
              )}
            </div>
            <div className="p-5">
              <p className="text-gray-700 mb-3 text-sm leading-relaxed">{phase.description}</p>
              <span className="text-purple-600 font-bold text-sm flex items-center gap-1">
                Explore Phase →
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Skill Recurrence - Compact */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-green-200">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <RefreshCw className="text-green-500" size={20} />
          How Skills Build: Spiral Learning
        </h3>
        <div className="flex items-center gap-2 mb-3 overflow-x-auto">
          {[
            { week: 'Week 1', label: 'Hear the sound', icon: '👂' },
            { week: 'Week 4', label: 'See it in books', icon: '👁️' },
            { week: 'Week 8', label: 'Trace and draw', icon: '✏️' },
            { week: 'Week 12', label: 'Create with it', icon: '🎨' }
          ].map((step, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <span className="text-gray-300 flex-shrink-0">→</span>}
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 flex-shrink-0">
                <span className="text-lg">{step.icon}</span>
                <div>
                  <div className="text-xs text-gray-500 font-bold">{step.week}</div>
                  <div className="text-xs text-gray-800 font-medium">{step.label}</div>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
        <p className="text-sm text-gray-600">
          Skills resurface every 3-4 weeks in fresh contexts — building familiarity and confidence without pressure.
        </p>
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-rose-500 rounded-xl p-4 sm:p-8 text-center text-white shadow-lg">
        <h3 className="text-2xl font-bold mb-2">Ready to Begin?</h3>
        <p className="text-lg opacity-95 mb-6 max-w-xl mx-auto">
          Choose your child's age group and start building a foundation for confident reading
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          className="bg-white text-purple-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-50 transition shadow-lg inline-flex items-center gap-2"
        >
          <Play size={20} />
          Start Learning Today
        </button>
      </div>
    </div>
  );

  // ─── Phase Detail ───────────────────────────────────────────
  const PhaseDetailView = () => {
    const phase = curriculumData[selectedAge];
    const isUserPhase = isPhaseActive(selectedAge);

    return (
      <div className="space-y-6">
        {/* Phase Header */}
        <div className={`bg-gradient-to-r ${phase.color} rounded-2xl p-4 sm:p-8 text-white shadow-lg`}>
          <button
            onClick={backToOverview}
            className="mb-4 flex items-center gap-2 text-white/80 hover:text-white transition bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg text-sm"
          >
            <ArrowLeft size={16} />
            All phases
          </button>
          <div className="flex items-center gap-5 flex-wrap">
            <img src={phase.icon} alt={phase.title} className="w-16 h-16 object-contain" />
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-bold mb-1">{phase.title}</h1>
              <p className="text-xl opacity-90">{phase.subtitle} — "{phase.theme}"</p>
            </div>
            {isUserPhase && (
              <div className="bg-white/20 rounded-lg px-5 py-3">
                <div className="text-sm opacity-90">Progress</div>
                <div className="text-2xl font-bold">Week {currentWeek}/52</div>
              </div>
            )}
          </div>
          <p className="text-lg opacity-90 mt-4 leading-relaxed">{phase.description}</p>
        </div>

        {/* Progress Bar (active phase only) */}
        {isUserPhase && (
          <div className="bg-white rounded-xl shadow-md p-5 border border-purple-200">
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="text-purple-500" size={20} />
              <h3 className="font-bold text-gray-900">Your Progress</h3>
            </div>
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Week {currentWeek} of 52</span>
              <span className="font-bold text-purple-600">{Math.round((currentWeek / 52) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${(currentWeek / 52) * 100}%` }}
              />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-gray-500 font-semibold">Completed</p>
                <p className="text-xl font-bold text-purple-600">{completedLessons.length} lessons</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-gray-500 font-semibold">Current Week</p>
                <p className="text-xl font-bold text-green-600">{currentWeek} of 52</p>
              </div>
            </div>
          </div>
        )}

        {/* What Your Child Will Explore */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="text-purple-500" size={22} />
            What Your Child Will Explore
          </h2>
          <ul className="space-y-2">
            {phase.focus.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <Check className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Parent Role */}
        <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Heart className="text-blue-500" size={20} />
            Your Role as a Parent
          </h3>
          <p className="text-gray-700 leading-relaxed">{phase.parentRole}</p>
        </div>

        {/* Quarterly Learning Path */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="text-purple-500" size={26} />
            Quarterly Learning Path
          </h2>

          {phase.quarters.map((quarter, qIdx) => {
            const [startWeek, endWeek] = quarter.weeks.split('-').map(Number);
            const isCurrentQuarter = isUserPhase && currentWeek >= startWeek && currentWeek <= endWeek;

            return (
              <div key={qIdx} className={`bg-white rounded-xl shadow-md overflow-hidden border ${isCurrentQuarter ? 'ring-2 ring-purple-500 border-purple-300' : 'border-gray-200'}`}>
                <button
                  onClick={() => setExpandedQuarter(expandedQuarter === qIdx ? null : qIdx)}
                  className={`w-full bg-gradient-to-r ${quarter.color} p-5 text-left text-white hover:opacity-95 transition`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="bg-white/20 px-3 py-1 rounded-full font-bold text-sm">
                          Weeks {quarter.weeks}
                        </span>
                        <h3 className="text-lg font-bold">{quarter.name}: {quarter.theme}</h3>
                        {isCurrentQuarter && (
                          <span className="bg-white/30 px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                            Current
                          </span>
                        )}
                      </div>
                      <p className="text-sm opacity-90">{quarter.description}</p>
                    </div>
                    {expandedQuarter === qIdx ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                  </div>
                </button>

                {expandedQuarter === qIdx && (
                  <div className="p-6 space-y-5">
                    {/* Week-by-Week Breakdown */}
                    <div>
                      <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                        <Calendar className="text-purple-500" size={16} />
                        Weekly Progression
                      </h4>
                      <div className="space-y-2">
                        {quarter.weekBreakdown.map((week, wIdx) => {
                          const [wStart, wEnd] = week.weeks.includes('-')
                            ? week.weeks.split('-').map(Number)
                            : [Number(week.weeks), Number(week.weeks)];
                          const isCurrentWeek = isUserPhase && currentWeek >= wStart && currentWeek <= wEnd;

                          return (
                            <div
                              key={wIdx}
                              className={`rounded-lg p-4 border-l-4 ${
                                isCurrentWeek
                                  ? 'border-purple-500 bg-purple-50'
                                  : 'border-gray-200 bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-bold text-purple-900 text-sm">Weeks {week.weeks}</span>
                                  <p className="text-gray-700 text-sm">{week.focus}</p>
                                </div>
                                {isCurrentWeek && (
                                  <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                                    <Play size={12} /> Now
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Skill Recurrence */}
                    {quarter.skillRecurrence && (
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2 text-sm">
                          <RefreshCw className="text-green-500" size={16} />
                          Skill Recurrence
                        </h4>
                        <p className="text-gray-700 text-sm">{quarter.skillRecurrence}</p>
                      </div>
                    )}

                    {/* Activities */}
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2 text-sm">
                        <Sparkles className="text-yellow-500" size={16} />
                        Sample Activities
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {quarter.activities.map((activity, aIdx) => (
                          <span key={aIdx} className="bg-yellow-50 border border-yellow-200 rounded-full px-3 py-1 text-sm text-gray-700">
                            {activity}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Enrichment */}
                    {quarter.enrichment && (
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2 text-sm">
                          <Sparkles className="text-purple-500" size={16} />
                          Enrichment Ideas
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {quarter.enrichment.map((activity, eIdx) => (
                            <span key={eIdx} className="bg-purple-50 border border-purple-200 rounded-full px-3 py-1 text-sm text-gray-700">
                              {activity}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Parent Tips */}
                    {quarter.parentGuidance && (
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2 text-sm">
                          <Heart className="text-blue-500" size={16} />
                          Parent Tip
                        </h4>
                        <p className="text-gray-700 text-sm">{quarter.parentGuidance}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Year-End Outcomes */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-green-200">
          <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Trophy className="text-yellow-500" size={22} />
            By the End of This Year
          </h2>
          <ul className="space-y-2">
            {phase.outcomes.map((outcome, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <div className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">✓</div>
                <span className="text-gray-700">{outcome}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Phase Navigation */}
        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
          <h3 className="font-bold text-gray-900 mb-3 text-sm">Explore Other Phases</h3>
          <div className="grid grid-cols-4 gap-2">
            {Object.entries(curriculumData).map(([ageKey, phaseData]) => {
              const isCurrentPhase = selectedAge === ageKey;
              const isActive = isPhaseActive(ageKey);

              return (
                <button
                  key={ageKey}
                  onClick={() => ageKey !== selectedAge && selectPhase(ageKey)}
                  disabled={isCurrentPhase}
                  className={`p-3 rounded-lg transition text-center ${
                    isCurrentPhase
                      ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-md cursor-default'
                      : 'bg-white hover:shadow-md text-gray-700 border border-gray-200'
                  } ${isActive && !isCurrentPhase ? 'ring-2 ring-green-400' : ''}`}
                >
                  <img src={phaseData.icon} alt={phaseData.title} className="w-10 h-10 object-contain mx-auto mb-1" />
                  <div className={`text-xs font-semibold ${isCurrentPhase ? 'text-white' : 'text-gray-600'}`}>
                    {phaseData.subtitle}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-3">
          <button
            onClick={backToOverview}
            className="flex-1 bg-white text-gray-900 rounded-xl p-4 shadow-md hover:shadow-lg transition border border-gray-200 font-semibold flex items-center justify-center gap-2"
          >
            <ArrowLeft size={18} />
            All Phases
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl p-4 shadow-md hover:shadow-lg transition font-semibold flex items-center justify-center gap-2"
          >
            <Play size={18} />
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  };

  // ─── Main Render ────────────────────────────────────────────
  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-amber-50 via-orange-50 to-pink-50">
      <nav className="bg-white/90 shadow-sm p-4 sticky top-0 z-50 border-b border-purple-100">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-2 rounded-xl shadow-md">
              <BookOpen className="text-white" size={24} />
            </div>
            <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              BrightReading
            </h1>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate('/about')} className="text-purple-600 hover:bg-purple-50 p-2 rounded-lg transition" title="About">
              <Info size={22} />
            </button>
            <button onClick={() => navigate('/dashboard')} className="text-purple-600 hover:bg-purple-50 p-2 rounded-lg transition" title="Dashboard">
              <Home size={22} />
            </button>
            <button onClick={() => navigate('/profile')} className="text-gray-600 hover:bg-gray-100 p-2 rounded-lg transition" title="Profile">
              <User size={22} />
            </button>
          </div>
        </div>
      </nav>

      <div ref={mainContainerRef} className="max-w-5xl mx-auto px-4 py-8">
        <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
          {selectedAge ? <PhaseDetailView /> : <OverviewSection />}
        </div>
      </div>
    </div>
  );
};

export default CurriculumPage;
